/** The employee portal's navigation, as data.
 *
 * Deliberately not the admin sidebar. The console's rail is a tool for someone
 * whose job is the service desk; this is for someone whose job is recruiting,
 * who came here because Outlook stopped syncing and wants to be gone in ninety
 * seconds. Same brand, same tokens, different information architecture.
 *
 * ## Why some rows say "not yet"
 *
 * Two destinations the portal brief asks for have no backend behind them, and
 * the audit that established that is recorded in `docs/PORTAL-BACKEND-GAPS.md`.
 * Approvals was a third until the manager sign-off API landed; the full-site
 * audit found the row still marked unbacked and the route still rendering a
 * capability state, which is the failure this comment exists to prevent.
 * They stay in the navigation with an honest state rather than being hidden,
 * for the same reason the console does it: a capability nobody can see is a
 * capability nobody can plan around. What they must never do is show a number.
 *
 * `backed` is the greppable split, so a row that gains an API is one edit here
 * and one route, not an audit of three components.
 */
import {
  Bell,
  BookOpen,
  CheckSquare,
  Home,
  LayoutGrid,
  type LucideIcon,
  Laptop,
  Ticket,
  UserCircle,
} from 'lucide-react'

export interface PortalNavItem {
  to: string
  label: string
  icon: LucideIcon
  /** Exact match. Only Home needs it. */
  end?: boolean
  /** False when the destination renders an honest "no API yet" state. */
  backed: boolean
}

export const PORTAL_NAV: PortalNavItem[] = [
  { to: '/', label: 'Home', icon: Home, end: true, backed: true },
  { to: '/tickets', label: 'My tickets', icon: Ticket, backed: true },
  { to: '/requests', label: 'My requests', icon: CheckSquare, backed: true },
  { to: '/catalog', label: 'Service catalog', icon: LayoutGrid, backed: true },
  { to: '/kb', label: 'Knowledge', icon: BookOpen, backed: true },
  { to: '/approvals', label: 'Approvals', icon: CheckSquare, backed: true },
  // The two below are navigation, not data. See docs/PORTAL-BACKEND-GAPS.md.
  { to: '/assets', label: 'My assets', icon: Laptop, backed: false },
  { to: '/notifications', label: 'Notifications', icon: Bell, backed: true },
  { to: '/profile', label: 'Profile', icon: UserCircle, backed: true },
]

/** Common employee problems, in the words an employee would use.
 *
 * These are search shortcuts, not a category list — the categories themselves
 * are database rows (`tickets.Service` / `ServiceSubcategory`, seeded by
 * migration 0006) and the portal reads them from `/api/portal/catalog/`.
 * Hardcoding a category list here would give the router and the SLA engine a
 * second source of truth they cannot see.
 *
 * The `q` is what gets typed into search; the real matching happens against
 * live catalog and knowledge data.
 */
export interface QuickIssue {
  label: string
  q: string
}

export const QUICK_ISSUES: QuickIssue[] = [
  { label: 'Outlook not syncing', q: 'outlook sync' },
  { label: 'Password reset', q: 'password' },
  { label: 'Teams audio or video', q: 'teams audio' },
  { label: 'VPN or remote access', q: 'vpn' },
  { label: 'Multi-factor authentication', q: 'authentication' },
  { label: 'New starter setup', q: 'new starter' },
]
